import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import { toast } from "../../../components/AppToast";
import { useDispatch, useSelector } from "react-redux";
import { daysOfWeek, parseOperationalHours, termsText } from "../constant/PartnerListColumns";
import { getGlobalOptions } from '../../../services/auth/selfSignUp';
import { enumGlobalOptionData } from '../../../utils/enumGlobalOptionData';
import { getallfacilitySelect } from "../../../services/facility/facility";
import { getAllModules } from "../../../services/tenant/tenant";
import { convertForSelect } from '../../../utils/convertApiData';
import AddressFields from "../../../components/AddressFields";
import { toPartnerApiStatus } from "../../../utils/partnerStatus";
import { buildOnboardPayload, buildStorerCreatePayload, displayStorerStatus, formStateFromStorerRecord, loggedInPartnerId, loggedInTenantId, validateStorerAccount, validateStorerForm } from "./storerForm";
import { onboardCustomer, searchCustomerById } from "../../../services/customer/customer";
import { onboardSupplier, searchSupplierById } from "../../../services/supplier/supplier";
import { countryList } from "../../../data/static";

function onboardDetailRows(record, prefix) {
    const address = record?.address || {};
    const place = [address[`${prefix}_city`], address[`${prefix}_state`], address[`${prefix}_country`]]
        .filter(Boolean)
        .join(", ");
    return [
        ["Name", record?.[`${prefix}_name`]],
        [`${prefix === "supplier" ? "Supplier" : "Customer"} ID`, record?.[`${prefix}_id`]],
        ["Email", record?.[`${prefix}_email`]],
        ["Contact", record?.[`${prefix}_mobile`]],
        ["Location", place],
        ["Status", displayStorerStatus(record?.status)],
    ].filter(([, value]) => String(value ?? "").trim());
}

function OnboardConfirmModal({ prompt, entityLabel, busy, onCancel, onConfirm }) {
    const prefix = prompt?.prefix || "customer";
    const record = prompt?.record || {};
    const name = record?.[`${prefix}_name`] || record?.[`${prefix}_id`] || entityLabel;
    const rows = onboardDetailRows(record, prefix);

    return (
        <Modal show={!!prompt} onHide={busy ? undefined : onCancel} centered backdrop="static">
            <Modal.Body className="px-4 pt-4 pb-2">
                <div className="d-flex align-items-start gap-3">
                    <span
                        className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 44, height: 44, background: "#eef2ff", color: "#3730a3" }}
                        aria-hidden
                    >
                        <i className={`bi ${prefix === "supplier" ? "bi-shop" : "bi-person-vcard"} fs-5`} />
                    </span>
                    <div>
                        <div className="text-uppercase small fw-semibold text-secondary mb-1" style={{ letterSpacing: "0.04em" }}>
                            {entityLabel} already exists
                        </div>
                        <h5 className="mb-1">{name}</h5>
                        <p className="text-muted mb-0">
                            This {entityLabel.toLowerCase()} ID is already on record. The form has been filled from it.
                            Are you sure you want to onboard?
                        </p>
                    </div>
                </div>
                <dl className="row g-2 mt-3 mb-0 border rounded-3 p-3 bg-light">
                    {rows.map(([label, value]) => (
                        <React.Fragment key={label}>
                            <dt className="col-4 small text-secondary fw-semibold mb-0">{label}</dt>
                            <dd className="col-8 small mb-0">{value}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            </Modal.Body>
            <Modal.Footer className="border-0 px-4 pb-4 pt-3">
                <button type="button" className="btn btn-outline-secondary px-4" onClick={onCancel} disabled={busy}>
                    No
                </button>
                <button type="button" className="btn btn-primary px-4" onClick={onConfirm} disabled={busy}>
                    {busy ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                            Onboarding
                        </>
                    ) : (
                        "Yes"
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

const operationalHoursInitial = daysOfWeek.reduce((acc, day) => {
    acc[day] = { enabled: false, open: "", close: "" };
    return acc;
}, {});

export default function CreatePartner({
    initialValues = {},
    onSubmit,
    submitText = "Save",
    cancelText = "",
    onCancel = () => { },
    readOnly = false,
    isEdit,
    /** "Partner" | "Customer" | "Supplier" — only changes display labels; field names stay identical. */
    entityLabel = "Partner",
    /** Customer / Supplier hide Type, Bank Details and Registration Documents. Partner shows all. */
    hiddenFieldNames = [],
    hideSections = [],
    hideDocuments = false,
    /** Field names that stay on the form but are not required (customer / supplier). */
    optionalFieldNames = [],
    /** Overrides the Address asterisks. Address line 2 is optional on every entity. */
    addressRequiredFields = ["address1", "city", "pincode", "state", "country"],
    /** Customer hides Operational Hours. Those hours are collected on Ship To. */
    hideOperationalHours = false,
    /** Customer / Supplier: only the storer API fields, with Active/Inactive status. */
    formVariant = "",
    fieldPrefix = "",
    hideTerms = false,
}) {
    const dispatch = useDispatch();
    const userDetail = useSelector((state) => state?.login?.userDetail);

    const [moduleOptions, setModulesData] = useState([]);
    const [facilities, setFacilityData] = useState([]);
    const [partnerTypes, setOrganizationtypeOptions] = useState([]);
    const [DocTypeData, setDocTypeData] = useState([]);

    const [formData, setFormData] = useState({
        partner_id: "",
        partner_name: "",
        partner_email: "",
        partner_mobile: "",
        partner_type: "",
        acceptTerms: false,
        bankName: "",
        ifscCode: "",
        accountNumber: "",
        partner_address1: "",
        partner_address2: "",
        partner_pincode: "",
        partner_postCode: "",
        partner_city: "",
        partner_state: "",
        partner_country: "",
        partner_noOfUserLicenses: "", // Or default number
        moduleAccess: [],
        facilityId: [],
        tenantId: "", // fill dynamically
        users: "",    // fill dynamically if needed
        Partner_OperationalDaysAndHours: {},
        Partner_Documents: [],
        username: "",
        password: "",
        status: "1",
        confirmPassword:'',
    });


    const [documentList, setDocumentList] = useState([{ docType: "", file: null }]);
    const [operationalHours, setOperationalHours] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [idSearching, setIdSearching] = useState(false);
    const idSearchLock = useRef(false);
    const lastLookupId = useRef("");
    const [onboardPrompt, setOnboardPrompt] = useState(null);
    const [onboarding, setOnboarding] = useState(false);
    const [errors, setErrors] = useState({});

    const countries = countryList;

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const orgTypeData = await getGlobalOptions(enumGlobalOptionData["PRT_TYPE"]);
                setOrganizationtypeOptions(convertForSelect(orgTypeData));

                const docTypes = await getGlobalOptions(enumGlobalOptionData["DOC_TYPE"]);
                setDocTypeData(convertForSelect(docTypes));

                const moduleTypeData = await getGlobalOptions(enumGlobalOptionData["SYS_CAP"]);
                setModulesData(convertForSelect(moduleTypeData));

                const data = await getallfacilitySelect();
                setFacilityData((data || []).map((f) => ({
                    label: f.Facility_ID,
                    value: f._id,
                })));
            } catch (err) {
                toast.error(err?.message || "Failed to fetch initial data");
            }
        };

        fetchInitialData();
    }, []);

    const isStorer = formVariant === "storer";

    const groupedFields = {
        [`${entityLabel} Details`]: [
            { label: `${entityLabel} ID`, name: "partner_id", type: "text", required: true, maxLength: isStorer ? 15 : undefined },
            { label: `${entityLabel} Name`, name: "partner_name", type: "text", required: true, maxLength: isStorer ? 45 : undefined },
            { label: `${entityLabel} Email`, name: "partner_email", type: "email", required: true },
            { label: "Contact Number", name: "partner_mobile", type: "text", required: isStorer || !optionalFieldNames.includes("partner_mobile") },
            {
                label: `${entityLabel} Type`,
                name: "partner_type",
                type: "select",
                required: true,
                options: partnerTypes,
            },
        ],
        Address: [
            { label: "Address Line 1", name: "partner_address1", type: "text", required: true },
            { label: "Address Line 2", name: "partner_address2", type: "text", required: false },
            { label: "Pin Code", name: "partner_pincode", type: "text", required: isStorer },
            { label: "Post Code", name: "partner_postCode", type: "text", required: false },

            { label: "City", name: "partner_city", type: "text", required: true },
            { label: "State", name: "partner_state", type: "text", required: isStorer || !optionalFieldNames.includes("partner_state") },
            {
                label: "Country",
                name: "partner_country",
                type: "select",
                required: true,
                options: countries,
            },
        ],
        "System Information": [
            { label: "Number of User Licenses", name: "partner_noOfUserLicenses", type: "text", required: true },
            {
                label: "Facility Access",
                name: "facilityId",
                type: "multiselect",
                required: false,
                options: facilities,
            },
            {
                label: "Module Access",
                name: "moduleAccess",
                type: "multiselect",
                required: true,
                options: moduleOptions,
            },
            {
                label: "Status",
                name: "status",
                type: "select",
                required: true,
                options: [
                    { label: "Active", value: "1" },
                    { label: "Pending", value: "2" },
                    { label: "Inactive", value: "3" },
                ],
            },
        ],
        "Bank Details": [
            { label: "Bank Name", name: "bankName", type: "text", required: false },
            { label: "Account Number", name: "accountNumber", type: "text", required: false },
            { label: "IFSC Code", name: "ifscCode", type: "text", required: false },
        ],
        "Password": [
            { label: "User name", name: "username", type: "text", required: true },
            { label: "Password", name: "password", type: "password", required: true },
            { label: "Confirm Password", name: "confirmPassword", type: "password", required: true },
        ]
    };

    const visibleGroupedFields = Object.fromEntries(
        Object.entries(groupedFields)
            .filter(([section]) => !hideSections.includes(section))
            .map(([section, fields]) => [
                section,
                fields.filter((field) => !hiddenFieldNames.includes(field.name)),
            ])
            .filter(([, fields]) => fields.length > 0)
    );


    useEffect(() => {
        if (!initialValues || Object.keys(initialValues).length === 0) return;
        console.log(initialValues, "initialValues");


        let PreFilledOperationalHrs = Array.isArray(initialValues.operationalHours)
            ? initialValues.operationalHours
            : parseOperationalHours(initialValues.operationalHours);

        let operationalHours = { ...operationalHoursInitial };

        if (Array.isArray(PreFilledOperationalHrs)) {
            PreFilledOperationalHrs.forEach((item) => {
                const day =
                    item.dayRange.charAt(0).toUpperCase() +
                    item.dayRange.slice(1).toLowerCase();

                // ✅ Only patch if the key exists in the initial structure
                if (Object.prototype.hasOwnProperty.call(operationalHoursInitial, day)) {
                    operationalHours[day] = {
                        ...operationalHours[day],
                        enabled: true, // ✅ keep naming consistent
                        open: item.timeRange?.open || operationalHours[day].open || "",
                        close: item.timeRange?.close || operationalHours[day].close || "",
                    };
                }
            });
        } else if (initialValues.operationalHours) {
            operationalHours = { ...initialValues.operationalHours };
        }

        const filteredOperationsal = Object.fromEntries(
            Object.entries(operationalHours).filter(([_, value]) => value.enabled)
        );


        const initialDocs = (initialValues.documents || []).map(doc => ({
            docType: doc.docType || "",
            file: doc.file || null,
        }));

        // Ensure at least one document row exists
        const documentListWithPlaceholder = initialDocs.length
            ? initialDocs
            : [{ docType: "", file: null }];

        console.log("initialValues", initialValues);

        setFormData((prev) => ({
            ...prev,
            partner_id: initialValues.partner_id || "",
            partner_name: initialValues.partner_name || "",
            partner_email: initialValues.partner_email || "",
            partner_mobile: initialValues.partner_mobile || "",
            partner_type: initialValues.partner_type || "",
            partner_noOfUserLicenses: initialValues.partner_noOfUserLicenses || "",
            bankName: initialValues["bank.bankName"] || "",
            ifscCode: initialValues["bank.ifscCode"] || "",
            accountNumber: initialValues["bank.accountNumber"] || "",
            partner_address1: initialValues["address.partner_address1"] || "",
            partner_address2: initialValues["address.partner_address2"] || "",
            partner_pincode: initialValues["address.partner_pincode"] || "",
            partner_postCode: initialValues["address.partner_postCode"] || "",
            partner_city: initialValues["address.partner_city"] || "",
            partner_state: initialValues["address.partner_state"] || "",
            partner_country: initialValues["address.partner_country"] || "",
            // moduleAccess: initialValues["moduleDetails.code"] || [],
            // facilityId: initialValues["facilityDetails.code"] || [],

            moduleAccess: initialValues["moduleDetails.code"]
                ? Array.isArray(initialValues["moduleDetails.code"])
                    ? initialValues["moduleDetails.code"]
                    : initialValues["moduleDetails.code"]
                        .split(",")
                        .map((v) => v.trim())
                : [],
            facilityId: initialValues["facilityDetails.code"]
                ? Array.isArray(initialValues["facilityDetails.code"])
                    ? initialValues["facilityDetails.code"]
                    : initialValues["facilityDetails.code"]
                        .split(",")
                        .map((v) => v.trim())
                : [],

            tenantId: initialValues.tenantId || "",
            users: initialValues.users || initialValues.createdBy || "",
            Partner_Documents: initialValues.documents || [],
            acceptTerms: initialValues.acceptTerms || false,
            status: String(toPartnerApiStatus(initialValues.status, 1)),
            username: initialValues.userDetails?.username || "",
            password: initialValues.userDetails?.password || "",
        confirmPassword:initialValues.userDetails?.password || ""
        }));



        setOperationalHours(filteredOperationsal);
        setDocumentList(documentListWithPlaceholder);


    }, [initialValues, isStorer]);


    // ===== Handlers =====
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const searchStorerById = async ({ quiet = false, typedId: typedIdOverride } = {}) => {
        const prefix = fieldPrefix || entityLabel.toLowerCase();
        const idKey = `${prefix}_id`;
        const typedId = String(typedIdOverride ?? formData.partner_id ?? "").trim();
        if (!typedId) {
            if (!quiet) {
                setErrors((prev) => ({ ...prev, partner_id: `${entityLabel} ID is required` }));
            }
            return;
        }
        if (quiet && lastLookupId.current === typedId.toLowerCase()) return;
        if (idSearchLock.current) return;
        idSearchLock.current = true;
        setIdSearching(true);
        try {
            const fetchList = prefix === "supplier" ? searchSupplierById : searchCustomerById;
            const res = await fetchList(0, 10, { [idKey]: typedId });
            const rows = Array.isArray(res?.data) ? res.data : [];
            const row =
                rows.find(
                    (r) => String(r?.[idKey] || "").trim().toLowerCase() === typedId.toLowerCase()
                ) || rows[0];
            lastLookupId.current = typedId.toLowerCase();
            if (!row) {
                if (!quiet) toast.error(`No ${entityLabel.toLowerCase()} found for this ID`);
                return;
            }
            const next = formStateFromStorerRecord(row, prefix);
            const { operationalHours: hours, documentList: docs, ...fields } = next;
            setFormData((prev) => ({ ...prev, ...fields }));
            setOperationalHours(hours);
            setDocumentList(docs);
            setErrors({});
            setOnboardPrompt({ record: row, prefix });
        } catch (err) {
            lastLookupId.current = "";
            toast.error(err?.message || `Failed to fetch ${entityLabel.toLowerCase()}`);
        } finally {
            idSearchLock.current = false;
            setIdSearching(false);
        }
    };

    const handleIdBlur = (e) => {
        if (readOnly || isEdit) return;
        if (e.relatedTarget?.closest?.("[data-storer-id-search]")) return;
        searchStorerById({ quiet: true, typedId: e.target.value });
    };

    const declineOnboard = () => {
        if (onboarding) return;
        setOnboardPrompt(null);
        onCancel({});
    };

    const confirmOnboard = async () => {
        if (!onboardPrompt || onboarding) return;
        const { record, prefix } = onboardPrompt;
        const partnerId = loggedInPartnerId(userDetail);
        const tenantId = loggedInTenantId(userDetail);
        if (!record?._id || !partnerId || !tenantId) {
            toast.error("Partner and tenant are required to onboard");
            return;
        }
        setOnboarding(true);
        try {
            const body = buildOnboardPayload(record, prefix, { partnerId, tenantId });
            await (prefix === "supplier" ? onboardSupplier(body) : onboardCustomer(body));
            const name = record?.[`${prefix}_name`] || record?.[`${prefix}_id`] || entityLabel;
            toast.success(`${name} was onboarded successfully`);
            setOnboardPrompt(null);
            onCancel({});
        } catch (err) {
            toast.error(err?.message || `Failed to onboard ${entityLabel.toLowerCase()}`);
        } finally {
            setOnboarding(false);
        }
    };




    const isPasswordMatch =
        formData.password &&
        formData.confirmPassword &&
        formData.password === formData.confirmPassword;


    const focusFirstInvalid = () => {
        requestAnimationFrame(() => {
            const firstErrorField = document.querySelector(".is-invalid");
            if (firstErrorField) {
                firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
                firstErrorField.focus?.();
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isStorer) {
            const storerErrors = {
                ...validateStorerForm(formData, entityLabel),
                ...validateStorerAccount(formData, operationalHours, {
                    requireOperationalHours: !hideOperationalHours,
                }),
            };
            if (Object.keys(storerErrors).length > 0) {
                setErrors(storerErrors);
                if (storerErrors.operationalHours) toast.warning(storerErrors.operationalHours);
                focusFirstInvalid();
                return;
            }
            setSubmitting(true);
            try {
                const prefix = fieldPrefix || entityLabel.toLowerCase();
                const documents = documentList
                    .filter((doc) => doc.file)
                    .map((doc) => ({ docType: doc.docType || "", file: doc.file }));
                await onSubmit(buildStorerCreatePayload(formData, prefix, {
                    operationalHours,
                    includeOperationalHours: !hideOperationalHours,
                    documents,
                    terms: termsText,
                }));
            } catch (error) {
                toast.error(error.message || `Failed to create ${entityLabel.toLowerCase()}`);
            } finally {
                setSubmitting(false);
            }
            return;
        }

        const newErrors = {};

        // 1️⃣ Dynamically get all required fields from groupedFields
        const requiredFields = Object.values(visibleGroupedFields)
            .flat()
            .filter((field) => field.required)
            .map(({ name, label }) => ({ key: name, label }));

        // 2️⃣ Validate required fields
        requiredFields.forEach(({ key, label }) => {
            const value = formData[key];
            if (
                value === undefined ||
                value === null ||
                value?.toString().trim() === "" ||
                (Array.isArray(value) && value.length === 0)
            ) {
                newErrors[key] = `${label} is required`;
            }
        });

        // 3️⃣ Email validation
        if (formData.partner_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.partner_email)) {
            newErrors.partner_email = "Please enter a valid email address";
        }

        // 5️⃣ Operational Hours check (global error) — not used on the customer screen
        if (!hideOperationalHours) {
            const openDays = Object.values(operationalHours).filter((d) => d.enabled);
            if (openDays.length === 0) {
                newErrors.operationalHours = "At least one operational day must be open";
            }
        }

        // 7️⃣ Terms acceptance (inline error)
        if (!formData.acceptTerms) {
            newErrors.acceptTerms = "Please accept Terms & Conditions";
        }

        // 8️⃣ Stop if there are errors
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);

            // Scroll & focus first invalid input
            const firstErrorField = document.querySelector(".is-invalid");
            if (firstErrorField) {
                firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
                firstErrorField.focus();
            }

            // Show toast only for global errors
            if (newErrors.operationalHours) toast.warning(newErrors.operationalHours);

            return;
        }

        // ✅ No errors: proceed
        setSubmitting(true);
        try {
            const filteredDocuments = documentList.filter((doc) => doc.file);
            const keyPrefix = entityLabel.toLowerCase();
            const field = (name) => `${keyPrefix}_${name}`;

            const payload = {
                [field("id")]: formData.partner_id,
                [field("name")]: formData.partner_name,
                [field("email")]: formData.partner_email,
                [field("mobile")]: formData.partner_mobile,
                [field("type")]: formData.partner_type,
                acceptTerms: formData.acceptTerms,
                bank: {
                    bankName: formData.bankName,
                    ifscCode: formData.ifscCode,
                    accountNumber: formData.accountNumber,
                },
                address: {
                    [field("address1")]: formData.partner_address1,
                    [field("address2")]: formData.partner_address2,
                    [field("pincode")]: formData.partner_pincode,
                    [field("city")]: formData.partner_city,
                    [field("state")]: formData.partner_state,
                    [field("country")]: formData.partner_country,
                },
                [field("noOfUserLicenses")]: formData.partner_noOfUserLicenses,
                moduleAccess: formData.moduleAccess,
                facilityId: formData.facilityId,
                terms: "This is terms",
                operationalHours: Object.entries(operationalHours)
                    .filter(([_, data]) => data.enabled)
                    .map(([day, data]) => ({
                        dayRange: day,
                        timeRange: { open: data.open, close: data.close },
                    })),
                documents: filteredDocuments,

                userDetails: {
                    username: formData.username,
                    password: formData.password,
                },
                status: toPartnerApiStatus(formData.status, 1),

            };

            console.log("✅ Final Payload:", payload);
            await onSubmit(payload);
        } catch (error) {
            toast.error(error.message || `Failed to create ${entityLabel.toLowerCase()}`);
        } finally {
            setSubmitting(false);
        }
    };


    // ===== Handlers =====


    const handleDayChange = (e, day) => {
        const enabled = e.target.checked;
        setOperationalHours((p) => ({
            ...p,
            [day]: enabled
                ? { ...p[day], enabled, open: p[day]?.open || "06:00", close: p[day]?.close || "23:00" }
                : { enabled: false, open: "", close: "" },
        }));
    };

    const handleTimeChange = (day, type, value) => {
        setOperationalHours((p) => ({
            ...p,
            [day]: { ...p[day], [type]: value },
        }));
    };

    const handleDocChange = (index, key, value) => {
        const updatedDocs = [...documentList];
        updatedDocs[index][key] = value;
        setDocumentList(updatedDocs);
    };

    const addMoreDoc = () => {
        setDocumentList((p) => [...p, { docType: "", file: null }]);
    };



    // ===== Render Form =====
    return (
        <form onSubmit={handleSubmit} className="p-4">
            {Object.entries(visibleGroupedFields).map(([section, fields]) => (
                <div key={section} className="border-bottom pb-3 mb-3">
                    <h6 className="fw-bold mb-3">{section}</h6>

                    {section === "Address" ? (


                        <AddressFields
                            prefix="partner_"
                            formData={formData}
                            onChange={handleChange}
                            errors={errors}
                            countries={countries}
                            requiredFields={addressRequiredFields}
                            includePostCode={isStorer}
                            readOnly={readOnly}
                        />
                    ) : (

                        <div className="row gx-3 gy-3">
                            {fields.map((field) => (
                                <div className={`col-md-3`} key={field.name}>
                                    <label
                                        htmlFor={field.name}
                                        className="form-label fw-semibold small mb-1"
                                        style={{ cursor: "pointer" }}
                                    >
                                        {field.label}
                                        {field.required && <span className="text-danger">*</span>}
                                    </label>

                                    {field.name === "password" && errors.password && (
                                        <div className="text-danger small mb-1">
                                            {errors.password}
                                        </div>
                                    )}
                                    {field.name === "confirmPassword" &&
                                        formData.confirmPassword &&
                                        !isPasswordMatch && (
                                            <div className="text-danger small mb-1">
                                                Passwords do not match
                                            </div>
                                        )}


                                    {/* === Select / Multi-select / Input Handling === */}
                                    {field.type === "select" ? (


                                        <select
                                            className={`form-select form-select-sm ${errors[field.name] ? "is-invalid" : ""}`}
                                            name={field.name}
                                            value={formData[field.name] || ""}
                                            onChange={handleChange}
                                            disabled={readOnly}
                                        >
                                            <option value="">Select {field.label}</option>
                                            {field.options?.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>

                                    ) : field.type === "multiselect" ? (



                                        <Select
                                            isMulti
                                            options={[{ label: "Select All", value: "*ALL*" }, ...field.options]}
                                            value={field.options.filter((o) => formData[field.name]?.includes(o.value))}
                                            onChange={(selected) => {
                                                const selectedValues = selected.map((s) => s.value);
                                                if (selectedValues.includes("*ALL*")) {
                                                    setFormData((p) => ({
                                                        ...p,
                                                        [field.name]:
                                                            p[field.name]?.length === field.options.length
                                                                ? []
                                                                : field.options.map((o) => o.value),
                                                    }));
                                                } else {
                                                    setFormData((p) => ({ ...p, [field.name]: selectedValues }));
                                                }
                                                setErrors((prev) => ({ ...prev, [field.name]: undefined }));
                                            }}
                                            className={errors[field.name] ? "is-invalid" : ""}
                                            classNamePrefix="react-select"
                                            isDisabled={readOnly}
                                        />


                                    ) : field.type === "label" ? (
                                        <p className="form-control-plaintext small fw-bold mb-0">{field.value}</p>
                                    ) : field.name === "partner_id" && isStorer ? (
                                        <div className="input-group input-group-sm">
                                            <input
                                                type="text"
                                                className={`form-control ${errors[field.name] ? "is-invalid" : ""}`}
                                                name={field.name}
                                                id={field.name}
                                                value={formData[field.name] || ""}
                                                onChange={(e) => {
                                                    lastLookupId.current = "";
                                                    handleChange(e);
                                                }}
                                                onBlur={handleIdBlur}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        searchStorerById();
                                                    }
                                                }}
                                                readOnly={readOnly}
                                                maxLength={field.maxLength}
                                                disabled={readOnly}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                data-storer-id-search=""
                                                onClick={() => searchStorerById()}
                                                disabled={readOnly || idSearching}
                                                title={`Search ${entityLabel}`}
                                            >
                                                {idSearching ? (
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                                                ) : (
                                                    <i className="bi bi-search" />
                                                )}
                                            </button>
                                        </div>
                                    ) : (


                                        <input
                                            type={field.type || "text"}
                                            className={`form-control form-control-sm ${field.name === "confirmPassword" &&
                                                formData.confirmPassword &&
                                                !isPasswordMatch
                                                ? "is-invalid"
                                                : errors[field.name]
                                                    ? "is-invalid"
                                                    : ""
                                                }`}

                                            name={field.name}
                                            id={field.name}
                                            value={formData[field.name] || ""}
                                            onChange={handleChange}
                                            readOnly={readOnly}
                                            maxLength={field.maxLength}
                                            disabled={field.name === "partner_id" && isEdit && !isStorer}
                                        />
                                    )}

                                    {errors[field.name] && (
                                        <div className="invalid-feedback d-block">
                                            {errors[field.name]}
                                        </div>
                                    )}

                                </div>


                            ))}


                        </div>


                    )}
                </div>
            ))}



            {/* Operational Hours Section — hidden on the customer screen */}


            {!hideOperationalHours && <div className="border-bottom pb-3 mb-3">
                <h6 className="fw-bold mb-3">Operational Hours</h6>
                <div className="row gx-3 gy-3">
                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                        <div key={day} className="col-md-3">
                            <div className="d-flex flex-column border rounded p-2 bg-light">
                                <div className="form-check mb-2">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={operationalHours[day]?.enabled || false}
                                        onChange={(e) => handleDayChange(e, day)}
                                        disabled={readOnly}
                                    />
                                    <label className="form-check-label small">{day}</label>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <input
                                        type="time"
                                        className="form-control form-control-sm me-1"
                                        value={operationalHours[day]?.open || "06:00"}
                                        disabled={!operationalHours[day]?.enabled || readOnly}
                                        onChange={(e) => handleTimeChange(day, "open", e.target.value)}
                                    />
                                    <input
                                        type="time"
                                        className="form-control form-control-sm ms-1"
                                        value={operationalHours[day]?.close || "23:00"}
                                        disabled={!operationalHours[day]?.enabled || readOnly}
                                        onChange={(e) => handleTimeChange(day, "close", e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>}



            {/* Registration Documents Section */}


            {!hideDocuments && <div className="border-bottom pb-3 mb-3">
                <h6 className="fw-bold mb-3">Registration Documents</h6>

                {Array.from({ length: Math.ceil(documentList.length / 2) }).map((_, rowIndex) => {
                    const firstIndex = rowIndex * 2;
                    const secondIndex = firstIndex + 1;

                    return (
                        <div key={rowIndex} className="row gx-3 gy-3 mb-2">
                            {[firstIndex, secondIndex].map((i) => {
                                if (i >= documentList.length) return null;
                                const doc = documentList[i];

                                return (
                                    <div key={i} className="col-md-6 d-flex h-100">
                                        <div className="d-flex w-100 gap-2 align-items-end">
                                            {/* Document Type */}
                                            <div style={{ flex: "0 0 45%" }} className="d-flex flex-column">
                                                <label className="form-label fw-semibold small mb-1">Document Type</label>
                                                <select
                                                    className="form-control"
                                                    value={doc.docType}
                                                    onChange={(e) => handleDocChange(i, "docType", e.target.value)}
                                                >
                                                    <option value="">Select Doc Type</option>
                                                    {DocTypeData.map((docTypeObj) => (
                                                        <option key={docTypeObj.value} value={docTypeObj.value}>
                                                            {docTypeObj.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* File Upload */}
                                            <div style={{ flex: "0 0 45%" }} className="d-flex flex-column">
                                                <label className="form-label fw-semibold small mb-1">Upload File</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    onChange={(e) => handleDocChange(i, "file", e.target.files[0])}
                                                />
                                            </div>

                                            {/* Buttons inline */}
                                            <div
                                                style={{ flex: "0 0 10%", height: "65%" }}
                                                className="d-flex gap-1 align-items-center justify-content-start"
                                            >
                                                {/* Add More only for last document */}
                                                {i === documentList.length - 1 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={addMoreDoc}
                                                    >
                                                        <i className="bi bi-plus-lg"></i>
                                                    </button>
                                                )}

                                                {/* Delete button if more than 1 document */}
                                                {documentList.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() =>
                                                            setDocumentList((prev) => prev.filter((_, index) => index !== i))
                                                        }
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>}





            {/* ✅ Terms & Conditions */}
            {!hideTerms && <div className="bg-light p-3 rounded">
                <h6 className="fw-bold mb-3">Terms & Conditions</h6>
                <div
                    className="border rounded p-2 mb-3"
                    style={{ maxHeight: "200px", overflowY: "auto", backgroundColor: "#fff" }}
                >
                    <p className="small mb-0">{termsText}</p>
                </div>
                <div className="form-check mb-3">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={formData.acceptTerms}
                        disabled={readOnly}
                        onChange={(e) => {
                            setFormData((p) => ({ ...p, acceptTerms: e.target.checked }));
                            setErrors((prev) => ({ ...prev, acceptTerms: undefined }));
                        }}

                        id="acceptTerms"
                    />
                    <label className="form-check-label small" htmlFor="acceptTerms">
                        I accept the Terms & Conditions
                    </label>
                </div>
            </div>}


            <div className="text-end mt-3 d-flex justify-content-center gap-2">
                {readOnly ? (
                    <button
                        type="button"
                        onClick={() => onCancel({})}
                        className="btn btn-secondary"
                    >
                        Back
                    </button>
                ) : (
                    <>
                        {cancelText && (
                            <button
                                type="button"
                                onClick={() => onCancel({})}
                                className="btn btn-secondary"
                            >
                                {cancelText}
                            </button>
                        )}
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting || (!hideTerms && !formData.acceptTerms)}
                        >
                            {submitText}
                        </button>
                    </>
                )}
            </div>

            <OnboardConfirmModal
                prompt={onboardPrompt}
                entityLabel={entityLabel}
                busy={onboarding}
                onCancel={declineOnboard}
                onConfirm={confirmOnboard}
            />

        </form>
    );
}